import React, { Suspense, useEffect } from 'react';
import './App.css';
import { Route, Routes, useLocation } from 'react-router-dom';
import Header from '../components/Header/Header.component';
import Footer from '../components/Footer/Footer.component';
import Clarity from '../components/Clarity/Clarity.component';
import worqRoutes from '../config/routes';
import { FooterColumns } from '../config/footer-columns';
import pages from '../config/pages';
import { initGA, trackPageView } from '../Utils/analytics';
// ============================================================================
// LAZY LOADED COMPONENTS - Following performance best practices
// ============================================================================

const Home = React.lazy(() => import('../pages/Home/Home.page'));
const Services = React.lazy(() => import('../pages/Services/Services.page'));
const Features = React.lazy(() => import('../pages/Features/Features.page'));
const PrivacyPolicy = React.lazy(() => import('../pages/PrivacyPolicy/PrivacyPolicy.page'));
const About = React.lazy(() => import('../pages/About/About.page'));
const ErrorPage = React.lazy(() => import('../pages/Error/Error.page'));

// Individual Feature Pages
const SecurityAccessControl = React.lazy(
    () => import('../pages/Features/SecurityAccessControl.page')
);
const SmartformsTemplates = React.lazy(() => import('../pages/Features/SmartformsTemplates.page'));
const ComplianceAuditAutomation = React.lazy(
    () => import('../pages/Features/ComplianceAuditAutomation.page')
);
const GeographicIntelligence = React.lazy(
    () => import('../pages/Features/GeographicIntelligence.page')
);
const OfflineReady = React.lazy(() => import('../pages/Features/OfflineReady.page'));
const IntegrationWithoutComplexity = React.lazy(
    () => import('../pages/Features/IntegrationWithoutComplexity.page')
);
const ReportingInsights = React.lazy(() => import('../pages/Features/ReportingInsights.page'));
const AssetManagement = React.lazy(() => import('../pages/Features/AssetManagement.page'));
const RiskManagement = React.lazy(() => import('../pages/Features/RiskManagement.page'));
const ComplianceRegulation = React.lazy(
    () => import('../pages/Features/ComplianceRegulation.page')
);
const Inspections = React.lazy(() => import('../pages/Features/Inspections.page'));

// ============================================================================
// LOADING COMPONENT - Following Single Responsibility Principle
// ============================================================================

const LoadingFallback: React.FC = () => (
    <div
        style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            fontSize: '1.2rem',
            color: '#666',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        }}
    >
        <div style={{ textAlign: 'center' }}>
            <div
                style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #3498db',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 16px',
                }}
            />
            Loading...
        </div>
        <style>
            {`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}
        </style>
    </div>
);

// ============================================================================
// MAIN APP COMPONENT - Following Single Responsibility Principle
// ============================================================================

const App: React.FC = () => {
    const location = useLocation();

    // Initialize Google Analytics and track page views
    useEffect(() => {
        // Initialize GA on app load
        initGA();
    }, []);

    // Track page views on route changes
    useEffect(() => {
        trackPageView(location.pathname + location.search);
    }, [location]);

    return (
        <>
            <Clarity projectId="t49kj0f7z1" enabled={process.env.NODE_ENV === 'production'} />
            <Header menuList={pages} />

            <div style={{ paddingTop: '80px' }}>
                <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                        <Route path={worqRoutes.home} element={<Home />} />
                        <Route path={worqRoutes.products} element={<Services />} />
                        <Route path={worqRoutes.solutions} element={<Features />} />
                        <Route path={worqRoutes.about} element={<About />} />
                        <Route path={worqRoutes.privacyPolicy} element={<PrivacyPolicy />} />

                        {/* Individual Feature Pages */}
                        <Route
                            path="/features/security-access-control"
                            element={<SecurityAccessControl />}
                        />
                        <Route
                            path="/features/smartforms-templates"
                            element={<SmartformsTemplates />}
                        />
                        <Route
                            path="/features/compliance-audit-automation"
                            element={<ComplianceAuditAutomation />}
                        />
                        <Route
                            path="/features/geographic-intelligence"
                            element={<GeographicIntelligence />}
                        />
                        <Route path="/features/offline-ready" element={<OfflineReady />} />
                        <Route
                            path="/features/integration-without-complexity"
                            element={<IntegrationWithoutComplexity />}
                        />
                        <Route
                            path="/features/reporting-insights"
                            element={<ReportingInsights />}
                        />
                        <Route path="/features/asset-management" element={<AssetManagement />} />
                        <Route path="/features/risk-management" element={<RiskManagement />} />
                        <Route
                            path="/features/compliance-regulation"
                            element={<ComplianceRegulation />}
                        />
                        <Route path="/features/inspections" element={<Inspections />} />

                        <Route path="*" element={<ErrorPage />} />
                    </Routes>
                </Suspense>
            </div>

            <Footer footerLists={FooterColumns} />
        </>
    );
};

export default App;
